import type { ReactElement, ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface RowActionsProps {
  readonly children: ReactNode;
  readonly className?: string;
}

/**
 * Wrapper for the row action cluster. Buttons stay visible so clinicians
 * discover edit/delete without hunting for hover affordances; a muted tone
 * keeps them from dominating the row and full contrast returns on
 * hover/focus.
 */
export function RowActions({ children, className }: RowActionsProps): ReactElement {
  return (
    <div
      className={cn(
        'flex items-center gap-1 shrink-0 opacity-70 group-hover:opacity-100 focus-within:opacity-100 transition-opacity',
        className,
      )}
    >
      {children}
    </div>
  );
}

interface RowActionButtonProps {
  readonly icon: ReactNode;
  readonly label: string;
  readonly onClick: () => void;
  readonly disabled?: boolean;
  /** Hover tooltip; defaults to `label`. Useful to explain a disabled state. */
  readonly title?: string;
}

/** Small ghost icon button used inside `<RowActions>`. */
export function RowActionButton({
  icon,
  label,
  onClick,
  disabled,
  title,
}: RowActionButtonProps): ReactElement {
  const tooltip = title ?? label;
  const button = (
    <Button
      size="sm"
      variant="ghost"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      // A disabled button has `pointer-events-none`, so its own `title` never shows on hover; the
      // wrapper span below carries the tooltip in that case.
      title={disabled ? undefined : tooltip}
      className="h-7 w-7 p-0"
    >
      {icon}
    </Button>
  );
  if (disabled) {
    return (
      <span title={tooltip} className="inline-flex">
        {button}
      </span>
    );
  }
  return button;
}
